import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import './LanguageToggle.css';

// Language Toggle Component
const LanguageToggle = ({ 
  variant = 'dropdown', // 'dropdown', 'buttons', 'flags', 'minimal'
  size = 'medium', // 'small', 'medium', 'large'
  showLabels = true,
  showFlags = true,
  showCurrentLanguage = true,
  position = 'bottom-right', // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
  className = '',
  onLanguageChange = null
}) => {
  const { t, language, setLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hoveredLanguage, setHoveredLanguage] = useState(null);
  const toggleRef = useRef(null);
  const dropdownRef = useRef(null);

  // Available languages
  const availableLanguages = [
    { code: 'bg', name: 'Български', flag: '/flag-bg.svg', shortName: 'BG' },
    { code: 'en', name: 'English', flag: '/flag-en.svg', shortName: 'EN' }
  ];

  // Handle language change
  const handleLanguageChange = useCallback((newLanguage) => {
    if (newLanguage !== language) {
      setIsAnimating(true);
      
      // Call custom handler if provided
      if (onLanguageChange) {
        onLanguageChange(newLanguage, language);
      }
      
      // Change language
      setLanguage(newLanguage);
      
      // Close dropdown
      setIsOpen(false);
      
      // Reset animation state
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  }, [language, setLanguage, onLanguageChange]);

  // Handle toggle click
  const handleToggleClick = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toggleRef.current && !toggleRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Focus management and mobile positioning
  useEffect(() => {
    if (isOpen && dropdownRef.current && toggleRef.current) {
      const focusableElements = dropdownRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
      
      // Handle mobile positioning - ensure dropdown is fully visible
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const toggleRect = toggleRef.current.getBoundingClientRect();
        const dropdown = dropdownRef.current;
        
        // Check if dropdown is in mobile menu (has arbiwin-header__nav-item--language parent)
        const isInMobileMenu = toggleRef.current.closest('.arbiwin-header__nav-item--language');
        
        if (isInMobileMenu) {
          // Use fixed positioning for mobile menu
          dropdown.style.position = 'fixed';
          dropdown.style.zIndex = '1002';
          
          // Calculate position - show above the button
          const dropdownHeight = dropdown.offsetHeight || 150; // Estimate if not rendered yet
          const spaceAbove = toggleRect.top;
          const spaceBelow = window.innerHeight - toggleRect.bottom;
          
          // If there's more space above, show above; otherwise show below
          if (spaceAbove > dropdownHeight || spaceAbove > spaceBelow) {
            // Show above
            dropdown.style.top = `${toggleRect.top - dropdownHeight - 8}px`;
            dropdown.style.bottom = 'auto';
          } else {
            // Show below
            dropdown.style.top = `${toggleRect.bottom + 8}px`;
            dropdown.style.bottom = 'auto';
          }
          
          // Horizontal positioning
          if (position.includes('right')) {
            dropdown.style.right = `${window.innerWidth - toggleRect.right}px`;
            dropdown.style.left = 'auto';
          } else {
            dropdown.style.left = `${toggleRect.left}px`;
            dropdown.style.right = 'auto';
          }
          
          // Ensure dropdown doesn't go off screen
          const maxWidth = Math.min(200, window.innerWidth - 16);
          dropdown.style.maxWidth = `${maxWidth}px`;
          dropdown.style.maxHeight = `${Math.min(300, window.innerHeight - 16)}px`;
          dropdown.style.overflowY = 'auto';
          
          // Scroll the mobile menu to show the dropdown if needed
          const mobileMenu = toggleRef.current.closest('.arbiwin-header__nav--mobile');
          const navList = mobileMenu?.querySelector('.arbiwin-header__nav-list');
          if (navList) {
            const languageItem = toggleRef.current.closest('.arbiwin-header__nav-item--language');
            if (languageItem) {
              languageItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }
      }
    }
  }, [isOpen, position]);

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return availableLanguages.find(lang => lang.code === language) || availableLanguages[0];
  };

  // Get language flag
  const getLanguageFlag = (langCode) => {
    const flags = {
      'bg': '🇧🇬',
      'en': '🇬🇧',
      'de': '🇩🇪',
      'fr': '🇫🇷',
      'es': '🇪🇸',
      'it': '🇮🇹',
      'pt': '🇵🇹',
      'ru': '🇷🇺',
      'tr': '🇹🇷',
      'ro': '🇷🇴'
    };
    return flags[langCode] || '🌐';
  };

  // Get language name
  const getLanguageName = (langCode) => {
    const names = {
      'bg': 'Български',
      'en': 'English',
      'de': 'Deutsch',
      'fr': 'Français',
      'es': 'Español',
      'it': 'Italiano',
      'pt': 'Português',
      'ru': 'Русский',
      'tr': 'Türkçe',
      'ro': 'Română'
    };
    return names[langCode] || langCode.toUpperCase();
  };

  const currentLanguage = getCurrentLanguageInfo();

  // Render dropdown variant
  const renderDropdown = () => (
    <div className={`language-toggle-dropdown ${className}`} ref={toggleRef}>
      <button
        onClick={handleToggleClick}
        className={`language-toggle-button ${size} ${isOpen ? 'open' : ''} ${isAnimating ? 'animating' : ''}`}
        aria-label={t('language.toggle.label')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="language-toggle-content">
          {showFlags && (
            <img 
              src={currentLanguage.flag} 
              alt={currentLanguage.name}
              className="language-flag"
            />
          )}
          {showCurrentLanguage && (
            <span className="language-name">
              {currentLanguage.shortName}
            </span>
          )}
          <span className="language-arrow">
            {isOpen ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`language-dropdown ${position} ${size}`}
          role="menu"
          aria-label={t('language.menu.label')}
        >
          <div className="language-dropdown-content">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`language-option ${lang.code === language ? 'active' : ''} ${hoveredLanguage === lang.code ? 'hovered' : ''}`}
                role="menuitem"
                aria-label={t('language.select', { language: getLanguageName(lang.code) })}
                onMouseEnter={() => setHoveredLanguage(lang.code)}
                onMouseLeave={() => setHoveredLanguage(null)}
              >
                <div className="language-option-content">
                  {showFlags && (
                    <span className="language-option-flag">
                      {getLanguageFlag(lang.code)}
                    </span>
                  )}
                  {showLabels && (
                    <span className="language-option-name">
                      {getLanguageName(lang.code)}
                    </span>
                  )}
                  {lang.code === language && (
                    <span className="language-option-check">
                      ✓
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render buttons variant
  const renderButtons = () => (
    <div className={`language-toggle-buttons ${className}`}>
      <div className="language-buttons-container">
        {availableLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`language-button ${size} ${lang.code === language ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
            aria-label={t('language.select', { language: getLanguageName(lang.code) })}
            aria-pressed={lang.code === language}
          >
            <div className="language-button-content">
              {showFlags && (
                <span className="language-button-flag">
                  {getLanguageFlag(lang.code)}
                </span>
              )}
              {showLabels && (
                <span className="language-button-name">
                  {getLanguageName(lang.code)}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Render flags variant
  const renderFlags = () => (
    <div className={`language-toggle-flags ${className}`}>
      <div className="language-flags-container">
        {availableLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`language-flag-button ${size} ${lang.code === language ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
            aria-label={t('language.select', { language: getLanguageName(lang.code) })}
            aria-pressed={lang.code === language}
            title={getLanguageName(lang.code)}
          >
            <img 
              src={lang.flag} 
              alt={lang.name}
              className="language-flag-emoji"
            />
            {lang.code === language && (
              <span className="language-flag-check">
                ✓
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Render minimal variant
  const renderMinimal = () => (
    <div className={`language-toggle-minimal ${className}`}>
      <button
        onClick={handleToggleClick}
        className={`language-minimal-button ${size} ${isOpen ? 'open' : ''} ${isAnimating ? 'animating' : ''}`}
        aria-label={t('language.toggle.label')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="language-minimal-flag">
          {getLanguageFlag(currentLanguage.code)}
        </span>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`language-minimal-dropdown ${position} ${size}`}
          role="menu"
          aria-label={t('language.menu.label')}
        >
          <div className="language-minimal-content">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`language-minimal-option ${lang.code === language ? 'active' : ''}`}
                role="menuitem"
                aria-label={t('language.select', { language: getLanguageName(lang.code) })}
                title={getLanguageName(lang.code)}
              >
                <span className="language-minimal-flag">
                  {getLanguageFlag(lang.code)}
                </span>
                {lang.code === language && (
                  <span className="language-minimal-check">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render based on variant
  switch (variant) {
    case 'buttons':
      return renderButtons();
    case 'flags':
      return renderFlags();
    case 'minimal':
      return renderMinimal();
    case 'dropdown':
    default:
      return renderDropdown();
  }
};

LanguageToggle.propTypes = {
  variant: PropTypes.oneOf(['dropdown', 'buttons', 'flags', 'minimal']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showLabels: PropTypes.bool,
  showFlags: PropTypes.bool,
  showCurrentLanguage: PropTypes.bool,
  position: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
  className: PropTypes.string,
  onLanguageChange: PropTypes.func
};

// Default exports
export default LanguageToggle;
